import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  ArrowUp,
  Check,
  LayoutDashboard,
  Loader2,
  Pencil,
  RotateCcw,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '../../components/ui/Button'
import { TextField } from '../../components/ui/Field'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Breadcrumb } from '../../components/Breadcrumb'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { api, extractApiError, isPlanLimitError } from '../../lib/api'
import { tenantWorkspacesResponse } from '../../lib/types'
import { formatDateTime } from '../../lib/format'


const workspaceSchema = z.object({
  name: z
    .string({ message: 'Workspace Name is required.' })
    .trim()
    .min(1, 'Workspace Name is required.')
    .min(2, 'Workspace Name must be at least 2 characters.')
    .max(100, 'Workspace Name must be at most 100 characters.'),
  status: z.enum(['active', 'inactive']).default('active'),
})

export function WorkspacesPage() {
  const qc = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  /** Create flow state.
   *
   *  • `createMode` — `'blank'` (workspace lands empty, same as the
   *                   legacy default template) or `'ai'` (the AI
   *                   prompt + textarea + Generate button surface).
   *                   Defaults to `'blank'` so the AI section is an
   *                   explicit opt-in rather than an always-visible
   *                   panel the user has to consciously ignore.
   *  • `prompt`     — what the user typed into the AI textarea.
   *  • `aiTemplate` — the most recent AI-generated structure
   *                   ({ spaces, counts, provenance }) or null. Sent
   *                   with the create payload when present.
   *  • `aiError`    — server-side error from the last generate
   *                   attempt so we can surface it inline. */
  const [createMode, setCreateMode] = useState('blank')
  const [prompt, setPrompt] = useState('')
  const [aiTemplate, setAiTemplate] = useState(null)
  const [aiError, setAiError] = useState(null)
  /** Two-step wizard for the create flow.
   *   • `1` — Name + Status + Mode chooser. Blank submits from here.
   *           "Create with AI" reveals the Next button which advances
   *           to step 2.
   *   • `2` — AI prompt + chips + Generate + preview. Reachable only
   *           when `createMode === 'ai'`. Footer shows "← Back" so
   *           the user can return to step 1 to adjust name / status
   *           or switch back to Blank.
   *  Edit mode always stays on step 1 — editing doesn't surface the
   *  AI wizard at all. */
  const [createStep, setCreateStep] = useState(1)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['workspaces', 'admin', 'all'],
    queryFn: async () => {
      const res = await api.get('/workspaces/admin/all')
      return tenantWorkspacesResponse.parse(res.data)
    },
  })

  const rows = data?.data ?? []

  const form = useForm({
    resolver: zodResolver(workspaceSchema),
    defaultValues: { name: '', status: 'active' },
  })

  function openCreate() {
    setEditing(null)
    form.reset({ name: '', status: 'active' })
    setCreateMode('blank')
    setCreateStep(1)
    setPrompt('')
    setAiTemplate(null)
    setAiError(null)
    setModalOpen(true)
  }

  // Dashboard shortcut: `?action=new` jumps straight into the create
  // dialog. Strips the param so refresh / back-button doesn't re-open
  // it. `useRef` guards against the React 18 StrictMode double-fire.
  const [searchParams, setSearchParams] = useSearchParams()
  const handledActionRef = useRef(false)
  useEffect(() => {
    if (handledActionRef.current) return
    if (searchParams.get('action') !== 'new') return
    handledActionRef.current = true
    openCreate()
    const next = new URLSearchParams(searchParams)
    next.delete('action')
    setSearchParams(next, { replace: true })
    // openCreate is stable enough — referencing it would mean memoising
    // the form reset, which isn't worth it for a once-per-mount effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams])

  function openEdit(row) {
    setEditing(row)
    form.reset({ name: row.name, status: row.status ?? 'active' })
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditing(null)
    setCreateMode('blank')
    setCreateStep(1)
    setPrompt('')
    setAiTemplate(null)
    setAiError(null)
    form.reset()
  }

  /** One-shot AI generation. Hits the new
   *  POST /workspace-templates/ai-generate endpoint with the user's
   *  prompt and stores the returned `{ spaces, counts, provenance }`
   *  in `aiTemplate`. Surfaces server errors inline next to the
   *  textarea (plan-cap, blank-output, network) rather than dropping
   *  a toast — the dialog is the user's focus, errors belong here. */
  const aiGenerateMutation = useMutation({
    mutationFn: async (rawPrompt) => {
      const res = await api.post(
        '/workspace-templates/ai-generate',
        { prompt: rawPrompt.trim() },
        // AI structure generation runs server-side against Anthropic and
        // can take 30–90s on Opus-tier models — override the 15s global
        // axios timeout so the browser waits for the result instead of
        // aborting it.
        { timeout: 120_000 },
      )
      return res.data
    },
    onSuccess: (data) => {
      setAiTemplate(data)
      setAiError(null)
    },
    onError: (err) => {
      if (isPlanLimitError(err)) {
        // Global interceptor already surfaces the "Upgrade plan"
        // toast — set a short inline note so the textarea panel
        // doesn't sit blank afterwards.
        setAiError('Monthly AI quota reached. Upgrade your plan to keep generating.')
        return
      }
      setAiError(extractApiError(err).message)
      setAiTemplate(null)
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (values) => {
      const payload = { name: values.name.trim() }
      // Always send status — both flows accept it. On create the
      // backend defaults to 'active' if omitted, but sending it
      // explicitly keeps the API contract symmetric with edit.
      if (values.status) payload.status = values.status
      if (editing) {
        await api.patch(`/workspaces/${editing.id}`, payload)
        return null
      }
      // Only send the AI-generated structure when the user explicitly
      // picked "Create with AI" AND actually generated a draft.
      // Switching back to "Blank" before submitting drops the
      // generated draft so a stray AI structure can't accidentally
      // seed a workspace the user wanted empty.
      if (createMode === 'ai' && aiTemplate?.spaces?.length) {
        payload.aiTemplate = { spaces: aiTemplate.spaces }
      }
      const res = await api.post('/workspaces', payload)
      return res.data ?? null
    },
    onSuccess: (data) => {
      if (editing) {
        toast.success('Workspace updated successfully.')
      } else if (data?.template) {
        const t = data.template
        const counts = []
        if (t.spaces > 0) counts.push(`${t.spaces} Space${t.spaces > 1 ? 's' : ''}`)
        if (t.lists > 0) counts.push(`${t.lists} List${t.lists > 1 ? 's' : ''}`)
        if (t.tasks > 0) counts.push(`${t.tasks} sample Task${t.tasks > 1 ? 's' : ''}`)
        const suffix = counts.length > 0 ? ` (${counts.join(', ')})` : ''
        // Distinguish the AI path from the legacy canned-template path
        // in the toast so the user knows whether the structure was
        // drafted from their prompt or pulled from a fixed catalogue.
        const verb = t.key === 'ai_generated' ? 'AI-generated' : 'created from template'
        toast.success(`Workspace ${verb}${suffix}.`)
      } else {
        toast.success('Workspace added successfully.')
      }
      closeModal()
      void qc.invalidateQueries({ queryKey: ['workspaces'] })
    },
    onError: (err) => {
      // Plan-limit errors are surfaced by the global axios interceptor
      // (with the "Upgrade plan" action button). Skip the local
      // toast.error so the user doesn't see two stacked messages —
      // one red error + one yellow warning saying the same thing.
      if (isPlanLimitError(err)) return
      const { message, fields } = extractApiError(err)
      let mapped = false
      if (fields) {
        for (const [name, msg] of Object.entries(fields)) {
          if (name in form.getValues()) {
            form.setError(name, { message: msg })
            mapped = true
          }
        }
      }
      if (!mapped) toast.error(message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/workspaces/${id}`)
    },
    onSuccess: () => {
      toast.success('Workspace deleted successfully.')
      setConfirmDelete(null)
      void qc.invalidateQueries({ queryKey: ['workspaces'] })
    },
    onError: (err) => {
      toast.error(extractApiError(err).message)
    },
  })

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Workspace Name',
        grow: true,
        pinnable: true,
        minWidth: 200,
        sortAccessor: (r) => (r.name ?? '').toLowerCase(),
        searchAccessor: (r) => r.name ?? '',
        exportValue: (r) => r.name ?? '',
        cellClassName: 'text-[color:var(--color-ink-strong)] font-medium',
        render: (r) => (
          <span className="block truncate" title={r.name}>
            {r.name}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 110,
        sortAccessor: (r) => r.status ?? '',
        exportValue: (r) => (r.status === 'inactive' ? 'Inactive' : 'Active'),
        render: (r) => (
          <span
            className={
              'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-white border border-transparent dark:border-white/10 ' +
              (r.status === 'inactive'
                ? 'bg-[color:var(--color-priority-urgent)] dark:bg-white/[0.06] dark:text-rose-300'
                : 'bg-[color:var(--color-success)] dark:bg-white/[0.06] dark:text-emerald-300')
            }
          >
            {r.status === 'inactive' ? 'Inactive' : 'Active'}
          </span>
        ),
      },
      {
        key: 'departmentCount',
        header: 'Departments',
        align: 'center',
        minWidth: 110,
        sortAccessor: (r) =>
          Number(r.departmentCount ?? r.departments?.length ?? 0),
        exportValue: (r) => r.departmentCount ?? 0,
        render: (r) => r.departmentCount ?? 0,
      },
      {
        key: 'memberCount',
        header: 'Members',
        align: 'center',
        minWidth: 100,
        sortAccessor: (r) => Number(r.memberCount ?? r.members?.length ?? 0),
        exportValue: (r) => r.memberCount ?? 0,
        render: (r) => r.memberCount,
      },
      {
        key: 'spaceCount',
        header: 'Spaces',
        align: 'center',
        minWidth: 100,
        sortAccessor: (r) => Number(r.spaceCount ?? r.spaces?.length ?? 0),
        exportValue: (r) => r.spaceCount ?? 0,
        render: (r) => r.spaceCount,
      },
      {
        key: 'lastActivityAt',
        header: 'Last Activity',
        minWidth: 150,
        sortAccessor: (r) => (r.lastActivityAt ? new Date(r.lastActivityAt) : null),
        exportValue: (r) => formatDateTime(r.lastActivityAt),
        cellClassName: 'text-[color:var(--color-ink-muted)]',
        render: (r) => formatDateTime(r.lastActivityAt),
      },
      {
        key: 'createdAt',
        header: 'Created On',
        minWidth: 150,
        sortAccessor: (r) => (r.createdAt ? new Date(r.createdAt) : null),
        exportValue: (r) => formatDateTime(r.createdAt),
        cellClassName: 'text-[color:var(--color-ink-muted)]',
        render: (r) => formatDateTime(r.createdAt),
      },
      {
        key: 'actions',
        header: 'Actions',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (r) => (
          <RowActionsMenu
            label={`Actions for ${r.name}`}
            items={[
              { key: 'edit', label: 'Edit', icon: Pencil, onClick: () => openEdit(r) },
              {
                key: 'delete',
                label: 'Delete',
                icon: Trash2,
                danger: true,
                separatorBefore: true,
                onClick: () => setConfirmDelete(r),
              },
            ]}
          />
        ),
      },
    ],
    // openEdit is a stable function declaration; setConfirmDelete is a stable
    // setter — the column set never needs to rebuild.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return (
    <div>
      <Breadcrumb current="Workspaces" />

      <div className="flex items-start justify-between mb-3">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)]">
            Workspaces
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Create and manage Workspaces — each one is a separate
            operational area, business unit, client environment, or
            team ecosystem.
          </p>
        </div>
      </div>

      {/* Client-side workspaces list on the shared DataTable — search / sort /
       *  pagination / export in the browser. Edit / Delete live in the row
       *  kebab; the create + AI-wizard flow stays in the modal below. */}
      <DataTable
        tableId="app-workspaces"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={isLoading}
        error={isError ? extractApiError(error).message : null}
        onRetry={() =>
          qc.invalidateQueries({ queryKey: ['workspaces', 'admin', 'all'] })
        }
        title="Workspaces"
        exportBaseName="workspaces"
        exportTitle="Workspaces"
        searchPlaceholder="Search by Workspace Name"
        defaultSort={[{ key: 'lastActivityAt', dir: 'desc' }]}
        emptyState={{ icon: LayoutDashboard, title: 'No Workspaces found' }}
      />

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={
          editing
            ? 'Edit Workspace'
            : createStep === 2
              ? 'Create with AI'
              : 'Add Workspace'
        }
        size="lg"
        footer={
          editing ? (
            // Edit flow — single page, single Save button.
            <>
              <Button
                variant="secondary"
                onClick={closeModal}
                disabled={saveMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                form="workspace-form"
                type="submit"
                loading={saveMutation.isPending}
              >
                Save Changes
              </Button>
            </>
          ) : createStep === 1 ? (
            // Step 1 — Cancel + (Add when Blank, Next when AI).
            //   • Blank flow: Add submits straight away.
            //   • AI flow:    Next opens step 2 (the AI prompt page).
            <>
              <Button
                variant="secondary"
                onClick={closeModal}
                disabled={saveMutation.isPending}
              >
                Cancel
              </Button>
              {createMode === 'ai' ? (
                <Button
                  type="button"
                  onClick={(e) => {
                    // Belt-and-braces: prevent any chance of the
                    // click event bubbling into a form submit on
                    // browsers that might mis-interpret it. Combined
                    // with `type="button"` on the underlying element,
                    // this guarantees we never accidentally fire
                    // saveMutation from this control.
                    e.preventDefault()
                    e.stopPropagation()
                    // Synchronous name check — direct read of the
                    // current value + length test. Avoids the async
                    // `form.trigger('name')` that was previously
                    // resolving false on valid input and silently
                    // blocking the advance.
                    const name = (form.getValues('name') ?? '').trim()
                    if (name.length === 0) {
                      form.setError('name', {
                        type: 'manual',
                        message: 'Workspace Name is required.',
                      })
                      return
                    }
                    if (name.length < 2) {
                      form.setError('name', {
                        type: 'manual',
                        message:
                          'Workspace Name must be at least 2 characters.',
                      })
                      return
                    }
                    form.clearErrors('name')
                    setCreateStep(2)
                  }}
                >
                  Next →
                </Button>
              ) : (
                <Button
                  form="workspace-form"
                  type="submit"
                  loading={saveMutation.isPending}
                >
                  Add
                </Button>
              )}
            </>
          ) : (
            // Step 2 — ← Back + Add. The Add button stays DISABLED
            // until the AI has actually returned a draft
            // (`aiTemplate.spaces.length > 0`). The previous behaviour
            // let the user submit an empty AI workspace by clicking
            // Add before generating, which is a confusing dead-end —
            // they'd land back on the workspace list with a workspace
            // that has no Spaces / Lists / Tasks despite picking the
            // "Create with AI" path. The button also stays disabled
            // while a generate request is in flight so a stale draft
            // can't be committed mid-stream.
            <>
              <Button
                variant="secondary"
                onClick={() => setCreateStep(1)}
                disabled={saveMutation.isPending || aiGenerateMutation.isPending}
              >
                ← Back
              </Button>
              <Button
                form="workspace-form"
                type="submit"
                loading={saveMutation.isPending}
                disabled={
                  aiGenerateMutation.isPending ||
                  !(aiTemplate?.spaces?.length > 0)
                }
                // Tooltip explains why the button is greyed before
                // the user has generated — without this they'd hover
                // a dead button and have no idea what's wrong.
                title={
                  aiTemplate?.spaces?.length > 0
                    ? 'Create this workspace with the AI-generated structure'
                    : 'Generate a draft first to enable Add'
                }
              >
                Add
              </Button>
            </>
          )
        }
      >
        {/* Single form spans both wizard steps. Hiding the inactive
         *  step's fields via `display: none` (rather than unmounting
         *  them) preserves React Hook Form state — switching back
         *  with the ← Back button still shows the typed name + status
         *  exactly as the user left them. */}
        <form
          id="workspace-form"
          // Submit-time guard: if the user is on step 1 with AI mode
          // selected and somehow triggers a submit (most commonly by
          // pressing Enter in the Name field — browsers auto-submit
          // a form with a single text input), we DO NOT create the
          // workspace. We advance to step 2 instead, so the AI flow
          // can't be silently bypassed. Every other state path
          // (Blank on step 1, AI on step 2, editing) submits normally.
          onSubmit={form.handleSubmit((v) => {
            if (!editing && createMode === 'ai' && createStep === 1) {
              setCreateStep(2)
              return
            }
            saveMutation.mutate(v)
          })}
          noValidate
          className="space-y-4"
        >
          {/* ── Step 1 ── Name + Status + Mode chooser ── */}
          <div className={createStep === 1 ? 'space-y-4' : 'hidden'}>
            <TextField
              label="Workspace Name"
              required
              placeholder="Enter a Workspace Name"
              minLength={2}
              maxLength={100}
              error={form.formState.errors.name?.message}
              {...form.register('name')}
            />
            {/* Status — Active (normal) or Inactive (archived,
             *  hidden from non-admin sidebars + read-only). */}
            <div>
              <label
                htmlFor="workspace-status"
                className="block w-fit text-[14px] font-medium text-[color:var(--color-ink-strong)] mb-1.5"
              >
                Status
              </label>
              <select
                id="workspace-status"
                {...form.register('status')}
                className="w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] text-sm outline-none transition-colors focus:border-[color:var(--color-accent)] cursor-pointer"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              {form.formState.errors.status?.message ? (
                <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                  {form.formState.errors.status.message}
                </p>
              ) : null}
            </div>
            {!editing ? (
              <CreateModeChooser
                mode={createMode}
                onChange={(next) => {
                  setCreateMode(next)
                  // Selecting a mode only toggles state — it does NOT
                  // advance the wizard. The Next button (visible in
                  // the footer when AI is selected) is what advances.
                  // Going back to Blank also clears any in-progress
                  // AI state so a stale draft can't sneak through.
                  if (next === 'blank') {
                    setCreateStep(1)
                    setPrompt('')
                    setAiTemplate(null)
                    setAiError(null)
                  }
                }}
              />
            ) : null}
          </div>

          {/* ── Step 2 ── AI prompt + Generate + preview ── */}
          {!editing && createMode === 'ai' ? (
            <div className={createStep === 2 ? '' : 'hidden'}>
              {/* Tiny summary so the user can see what they're about
               *  to create without going back to step 1. */}
              <div className="mb-4 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-[color:var(--color-ink-muted)] font-semibold">
                    Workspace
                  </p>
                  <p className="text-sm font-semibold text-[color:var(--color-ink-strong)] truncate">
                    {form.watch('name') || 'Untitled'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCreateStep(1)}
                  className="text-xs text-[color:var(--color-accent-strong)] hover:underline cursor-pointer whitespace-nowrap"
                >
                  Edit Name / Status
                </button>
              </div>
              <AiTemplatePicker
                prompt={prompt}
                onPromptChange={setPrompt}
                aiTemplate={aiTemplate}
                aiError={aiError}
                isGenerating={aiGenerateMutation.isPending}
                onGenerate={() => aiGenerateMutation.mutate(prompt)}
                onClear={() => {
                  setAiTemplate(null)
                  setAiError(null)
                }}
              />
            </div>
          ) : null}
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Workspace"
        message={
          confirmDelete
            ? `Are you sure you want to delete "${confirmDelete.name}"? This will permanently remove every Space, List, Task and Department inside it. This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteMutation.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) deleteMutation.mutate(confirmDelete.id)
        }}
      />
    </div>
  )
}

/**
 * Two-card chooser at the top of the create form: Blank vs Create
 * with AI. Acts as a radio group (aria-checked + keyboard arrow
 * navigation isn't strictly required for two-option chooser, but
 * standard role="radio" semantics are still applied so screen
 * readers announce the selection state correctly).
 *
 * The Blank card carries a `LayoutDashboard` icon to echo the legacy
 * "Blank" template; the AI card uses the same Sparkles icon used
 * elsewhere in the dialog so the visual identity stays consistent.
 */
function CreateModeChooser({ mode, onChange }) {
  return (
    <div role="radiogroup" aria-label="How to start the workspace">
      <span className="block text-base font-semibold text-[color:var(--color-ink-strong)] mb-2.5">
        How would you like to start?
      </span>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <ModeCard
          selected={mode === 'blank'}
          onClick={() => onChange('blank')}
          icon={LayoutDashboard}
          title="Blank workspace"
          description="Just the workspace. Add Spaces, Lists and Tasks yourself."
        />
        <ModeCard
          selected={mode === 'ai'}
          onClick={() => onChange('ai')}
          icon={Sparkles}
          title="Create with AI"
          description="Describe your team — AI drafts the Spaces, Lists and sample Tasks."
          accent
        />
      </div>
    </div>
  )
}

/** Single selectable card in the mode chooser. `accent` adds the
 *  AI-violet accent tint so the AI option visually stands apart from
 *  the neutral Blank option. */
function ModeCard({ selected, onClick, icon: Icon, title, description, accent }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className={
        // `min-h-[112px]` + `p-4` give the cards real visual weight —
        // they're the primary decision in the dialog and felt squat
        // at the previous `p-3` density. The flex column wrapper lets
        // a Bigger icon chip sit above the title so the card has a
        // clear top→bottom rhythm instead of a single cramped row.
        'flex flex-col text-left rounded-lg border p-4 min-h-[120px] cursor-pointer transition-all ' +
        (selected
          ? accent
            ? 'border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] shadow-sm'
            : 'border-[color:var(--color-accent-strong)] bg-[color:var(--color-card)] shadow-sm ring-1 ring-[color:var(--color-accent-strong)]'
          : 'border-[color:var(--color-rule)] bg-[color:var(--color-card)] hover:border-[color:var(--color-accent-border)] hover:bg-[color:var(--color-card-hover)]')
      }
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className={
            'h-11 w-11 rounded-lg inline-flex items-center justify-center shrink-0 ' +
            (accent
              ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]'
              : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]')
          }
        >
          <Icon size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-[color:var(--color-ink-strong)] flex items-center gap-1.5">
            {title}
            {selected ? (
              <Check
                size={15}
                className="text-[color:var(--color-accent-strong)] shrink-0"
              />
            ) : null}
          </p>
          <p className="text-sm text-[color:var(--color-ink-muted)] mt-1 leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </button>
  )
}

/**
 * AI template picker — replaces the old canned-template grid.
 *
 * Two states:
 *   1. **Idle** — textarea + "Generate with AI" button. Hint text
 *      with example prompts so the user knows what to type.
 *   2. **Generated** — preview panel showing space/list/task counts
 *      plus the actual Space tree (collapsible), with "Use a
 *      different prompt" + "Regenerate" buttons.
 *
 *  Skip both: leave the prompt empty + don't generate, and the
 *  workspace is created blank (same as the legacy "Blank" template).
 *
 *  All state lives in the parent so closing the modal cleanly
 *  resets it. The component is pure-presentation + raises events.
 */
/** Quick-start chip suggestions for the AI prompt. Clicking one
 *  fills the textarea with a fully-formed sentence the user can
 *  expand on, removing the "blank-page" friction. The four cover
 *  the common TaskMagix use-cases without taking up too much room
 *  in the dialog. */
const PROMPT_SUGGESTIONS = [
  {
    label: 'Customer support',
    prompt: 'Customer support team handling email and WhatsApp tickets',
  },
  {
    label: 'Marketing campaigns',
    prompt: 'Marketing team running monthly campaigns across email and social',
  },
  {
    label: 'Engineering sprint',
    prompt: 'Engineering team running two-week sprints with backlog grooming',
  },
  {
    label: 'HR onboarding',
    prompt: 'HR team onboarding new hires through their first 90 days',
  },
]

/** Recursive renderer for a task node + its nested subtasks. Used by
 *  the AI workspace preview so the user sees the full task tree
 *  before accepting. Indent scales with depth; arrow glyph changes
 *  per level so the hierarchy is scannable without colour. */
function AiTemplateTaskNode({ task, depth = 0 }) {
  const indentClass =
    depth === 0 ? '' : depth === 1 ? 'ml-4' : 'ml-8'
  const glyph = depth === 0 ? '•' : depth === 1 ? '↳' : '⤷'
  return (
    <li className={indentClass}>
      <span className="text-[color:var(--color-ink)]">
        <span className="text-[color:var(--color-ink-subtle)] mr-1">
          {glyph}
        </span>
        {task.name}
      </span>
      {Array.isArray(task.subtasks) && task.subtasks.length > 0 ? (
        <ul className="mt-0.5 space-y-0.5">
          {task.subtasks.map((st) => (
            <AiTemplateTaskNode
              key={st.name}
              task={st}
              depth={depth + 1}
            />
          ))}
        </ul>
      ) : null}
    </li>
  )
}

function AiTemplatePicker({
  prompt,
  onPromptChange,
  aiTemplate,
  aiError,
  isGenerating,
  onGenerate,
  onClear,
}) {
  const hasGenerated = aiTemplate?.spaces?.length > 0
  // Activate the arrow as soon as ANY text is in the box. The
  // backend still requires ≥ 10 chars to actually generate — if
  // the user submits something shorter, they get a clear inline
  // error from the API. Gating the button at 10 chars made it
  // look "stuck" to users typing a couple of characters; this
  // pattern matches Gemini / ChatGPT composers where the send
  // arrow lights up the moment you start typing.
  const canGenerate = !isGenerating && prompt.trim().length > 0

  return (
    <div>
      {/* AI prompt card — chat-composer style: the textarea grows
       *  vertically and the Generate action lives as a small blue
       *  arrow button anchored to the bottom-right of the textarea,
       *  mirroring the Gemini / ChatGPT input box pattern users are
       *  already fluent with. The "Regenerate" / "Generate with AI"
       *  text button is gone — the same arrow handles both cases
       *  (a re-press after generating becomes a regenerate). */}
      <div className="rounded-xl border border-[color:var(--color-accent-border)] bg-gradient-to-br from-[color:var(--color-accent-bg)] to-[color:var(--color-card)] p-4">
        <label
          htmlFor="workspace-ai-prompt"
          className="block w-fit text-sm font-semibold text-[color:var(--color-ink-strong)] mb-2"
        >
          Describe your team or workflow
        </label>

        {/* Composer — textarea + send arrow stacked in a relative
         *  container. The textarea reserves right-side padding for
         *  the arrow so long lines don't slide behind it. */}
        <div
          className={
            'relative rounded-lg border bg-[color:var(--color-card)] transition-colors focus-within:border-[color:var(--color-accent)] ' +
            (aiError
              ? 'border-[color:var(--color-priority-urgent)]'
              : 'border-[color:var(--color-rule)]')
          }
        >
          <textarea
            id="workspace-ai-prompt"
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            onKeyDown={(e) => {
              // Cmd/Ctrl + Enter submits — common keyboard pattern for
              // chat composers. Plain Enter inserts a newline (default
              // textarea behaviour) so multi-line prompts work.
              if (
                (e.metaKey || e.ctrlKey) &&
                e.key === 'Enter' &&
                canGenerate
              ) {
                e.preventDefault()
                onGenerate()
              }
            }}
            placeholder="What does this team do? e.g. Customer support handling email tickets, or Marketing running monthly campaigns"
            rows={3}
            maxLength={1000}
            disabled={isGenerating}
            className="w-full px-3 py-2.5 pr-14 rounded-lg bg-transparent text-[color:var(--color-ink)] text-sm placeholder:text-[color:var(--color-ink-subtle)] placeholder:text-xs outline-none disabled:opacity-60 disabled:cursor-not-allowed resize-none"
          />
          {/* Send arrow — blue circular button anchored bottom-right.
           *  Disabled when the prompt is too short or while a previous
           *  generation is still in flight. The icon swaps to a
           *  spinner during the in-flight state so the user has clear
           *  feedback that something is happening. */}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (canGenerate) onGenerate()
            }}
            disabled={!canGenerate}
            title={
              isGenerating
                ? 'Generating…'
                : hasGenerated
                  ? 'Regenerate from this prompt'
                  : 'Generate workspace structure'
            }
            aria-label={hasGenerated ? 'Regenerate' : 'Generate'}
            className={
              'absolute right-2 bottom-2 h-9 w-9 rounded-full inline-flex items-center justify-center shadow-sm transition-all ' +
              (canGenerate
                ? 'bg-[color:var(--color-accent-strong)] text-white hover:brightness-110 cursor-pointer'
                : 'bg-[color:var(--color-rule)] text-[color:var(--color-ink-subtle)] cursor-not-allowed')
            }
          >
            {isGenerating ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <ArrowUp size={16} strokeWidth={2.5} />
            )}
          </button>
        </div>

        {/* Suggestion chips + character counter + Clear link. The row
         *  shifts contents depending on state:
         *   • Before generation — chips on the left, counter on the
         *     right.
         *   • After generation — "Clear & start over" link replaces
         *     the chips so the user can drop the draft and try a
         *     different prompt. */}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {hasGenerated ? (
              <button
                type="button"
                onClick={onClear}
                disabled={isGenerating}
                className="text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] cursor-pointer transition-colors inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <RotateCcw size={12} />
                Clear &amp; start over
              </button>
            ) : (
              <>
                <span className="text-xs text-[color:var(--color-ink-muted)]">
                  Try:
                </span>
                {PROMPT_SUGGESTIONS.map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    disabled={isGenerating}
                    onClick={() => onPromptChange(s.prompt)}
                    className="text-xs px-2 py-1 rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] hover:border-[color:var(--color-accent-border)] cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {s.label}
                  </button>
                ))}
              </>
            )}
          </div>
          <span className="text-[11px] text-[color:var(--color-ink-subtle)] tabular-nums shrink-0">
            {prompt.length}/1000
          </span>
        </div>

        {!hasGenerated && prompt.trim().length > 0 && prompt.trim().length < 10 ? (
          <p className="mt-2 text-xs text-[color:var(--color-ink-muted)]">
            Tip: a sentence or two produces a sharper draft.
          </p>
        ) : null}

        {aiError ? (
          <p
            role="alert"
            className="mt-3 text-sm text-[color:var(--color-priority-urgent)] flex items-start gap-1.5"
          >
            <span aria-hidden="true">⚠</span>
            <span>{aiError}</span>
          </p>
        ) : null}
      </div>

      {/* Loading placeholder — sits BELOW the AI card so the card's
       *  controls remain visible (the user can read what they typed
       *  while waiting). */}
      {isGenerating ? (
        <div className="mt-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
          <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
            <Loader2
              size={14}
              className="animate-spin text-[color:var(--color-accent-strong)]"
            />
            Drafting your workspace structure…
          </p>
          <div className="mt-3 space-y-2 animate-pulse">
            <div className="h-3 w-2/3 rounded bg-[color:var(--color-rule)]" />
            <div className="h-2.5 w-1/2 rounded bg-[color:var(--color-rule)]" />
            <div className="h-2.5 w-3/4 rounded bg-[color:var(--color-rule)]" />
          </div>
        </div>
      ) : null}

      {/* Generated preview — counts row + Space/List tree. Read-only;
       *  the user accepts as-is by submitting the form, or
       *  Regenerates / Clears if they want a different shape. */}
      {hasGenerated && !isGenerating ? (
        <div className="mt-3 rounded-xl border border-[color:var(--color-accent-border)] bg-[color:var(--color-card)] p-4">
          <div className="flex items-center justify-between gap-3 pb-3 border-b border-[color:var(--color-rule)]">
            <p className="text-sm font-semibold text-[color:var(--color-accent-strong)] inline-flex items-center gap-1.5">
              <Sparkles size={14} />
              AI draft ready
            </p>
            {aiTemplate?.counts ? (
              <p className="text-xs text-[color:var(--color-ink-muted)] tabular-nums">
                <strong className="text-[color:var(--color-ink-strong)] font-semibold">
                  {aiTemplate.counts.spaces}
                </strong>{' '}
                Spaces ·{' '}
                <strong className="text-[color:var(--color-ink-strong)] font-semibold">
                  {aiTemplate.counts.lists}
                </strong>{' '}
                Lists
                {aiTemplate.counts.tasks > 0 ? (
                  <>
                    {' · '}
                    <strong className="text-[color:var(--color-ink-strong)] font-semibold">
                      {aiTemplate.counts.tasks}
                    </strong>{' '}
                    sample Tasks
                  </>
                ) : null}
              </p>
            ) : null}
          </div>
          <ul className="mt-3 space-y-2.5">
            {aiTemplate.spaces.map((sp) => (
              <li key={sp.name}>
                <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                  {sp.name}
                </p>
                {sp.description ? (
                  <p className="text-xs text-[color:var(--color-ink-muted)] mt-0.5">
                    {sp.description}
                  </p>
                ) : null}
                {sp.lists?.length > 0 ? (
                  <ul className="mt-1.5 ml-3 space-y-1.5">
                    {sp.lists.map((lst) => (
                      <li
                        key={lst.name}
                        className="text-xs text-[color:var(--color-ink)]"
                      >
                        <span className="font-medium text-[color:var(--color-ink-strong)]">
                          ↳ {lst.name}
                        </span>
                        {lst.tasks?.length > 0 ? (
                          <ul className="mt-1 ml-4 space-y-0.5">
                            {lst.tasks.map((t) => (
                              <AiTemplateTaskNode
                                key={t.name}
                                task={t}
                              />
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
          <p className="mt-3 pt-3 border-t border-[color:var(--color-rule)] text-xs text-[color:var(--color-ink-muted)]">
            Click <strong className="text-[color:var(--color-ink-strong)] font-semibold">Add</strong> to create this workspace with the structure above. You can edit or delete anything afterwards.
          </p>
        </div>
      ) : null}

    </div>
  )
}
